'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, CheckCheck, UserCheck, AlertCircle, Clock, ExternalLink } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

interface NotificationItem {
  id: string;
  type: string;
  title: string;
  message: string;
  data: Record<string, any>;
  read_at: string | null;
  is_read: boolean;
  created_at: string;
  timestamp: string;
}

export function NotificationBell() {
  const router = useRouter();
  const { isAuthenticated, isAdmin } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated || !isAdmin) return;
    try {
      const res = await api.get<any>('/notifications');
      setUnreadCount(res?.unread_count || res?.data?.unread_count || 0);
      const list = res?.notifications || res?.data?.notifications || [];
      setNotifications(Array.isArray(list) ? list : []);
    } catch {
      setNotifications([]);
    }
  }, [isAuthenticated, isAdmin]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 20000); // 20s poll
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Click outside listener
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleMarkAsRead = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await api.post(`/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // Ignore
    }
  };

  const handleMarkAllAsRead = async () => {
    setIsLoading(true);
    try {
      await api.post('/notifications/read-all');
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch {
      // Ignore
    } finally {
      setIsLoading(false);
    }
  };

  const handleNavigateToAdmin = (tab: string = 'verifikasi', notificationId?: string, notificationData?: any) => {
    if (notificationId) {
      handleMarkAsRead(notificationId, { stopPropagation: () => {} } as any);
    }
    setIsOpen(false);
    const nimParam = notificationData?.nim ? `&nim=${encodeURIComponent(notificationData.nim)}` : '';
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('switch-admin-tab', {
          detail: {
            tab,
            nim: notificationData?.nim || '',
            name: notificationData?.name || '',
            userId: notificationData?.user_id || '',
          },
        })
      );
    }
    router.push(`/admin?tab=${tab}${nimParam}`);
  };

  if (!isAuthenticated || !isAdmin) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-xl text-foreground/80 hover:text-foreground hover:bg-muted/80 transition-all focus:outline-none focus:ring-2 focus:ring-[#004A99]/40"
        title="Notifikasi Masuk"
        aria-label="Notifikasi"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex items-center justify-center min-w-4 h-4 px-1 text-[10px] font-bold text-white bg-[#D71920] rounded-full ring-2 ring-background animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Popover Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 rounded-2xl bg-card/95 backdrop-blur-xl border border-[#E2E8F0] shadow-2xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E2E8F0] bg-slate-50">
            <div className="flex items-center gap-2">
              <span className="font-heading font-bold text-sm text-[#082B52]">Notifikasi Admin</span>
              {unreadCount > 0 && (
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-50 text-[#D71920] border border-red-200">
                  {unreadCount} Baru
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                disabled={isLoading}
                className="text-xs text-[#64748B] hover:text-[#004A99] transition-colors flex items-center gap-1 font-medium"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Tandai dibaca
              </button>
            )}
          </div>

          {/* List Content */}
          <div className="max-h-80 overflow-y-auto divide-y divide-[#E2E8F0]">
            {(!notifications || notifications.length === 0) ? (
              <div className="py-8 px-4 text-center text-muted-foreground space-y-2">
                <Bell className="w-8 h-8 mx-auto opacity-30 stroke-[1.5]" />
                <p className="text-xs">Belum ada notifikasi pendaftaran.</p>
              </div>
            ) : (
              (notifications || []).map((n) => (
                <div
                  key={n.id}
                  onClick={() => handleNavigateToAdmin('verifikasi', n.id, n.data)}
                  className={`p-3.5 transition-all hover:bg-blue-50/80 cursor-pointer flex items-start gap-3 text-left ${
                    !n.is_read ? 'bg-blue-50/50' : ''
                  }`}
                >
                  <div
                    className={`mt-0.5 p-2 rounded-xl shrink-0 ${
                      !n.is_read
                        ? 'bg-blue-100 text-[#004A99]'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    <UserCheck className="w-4 h-4" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1">
                      <p className="text-xs font-semibold text-[#1F2937] truncate">{n.title}</p>
                      {!n.is_read && (
                        <button
                          onClick={(e) => handleMarkAsRead(n.id, e)}
                          title="Tandai telah dibaca"
                          className="text-[10px] text-[#64748B] hover:text-[#004A99] shrink-0"
                        >
                          <CheckCheck className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-[#64748B] mt-0.5 line-clamp-2 leading-relaxed">
                      {n.message}
                    </p>
                    <div className="flex items-center gap-2 mt-1.5 text-[10px] text-[#64748B]">
                      <Clock className="w-3 h-3" />
                      <span>{n.created_at}</span>
                      {n.data?.nim && (
                        <span className="font-mono bg-slate-100 px-1 rounded">
                          NIM: {n.data.nim}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer Action */}
          <div className="p-2 border-t border-[#E2E8F0] bg-slate-50 text-center">
            <button
              type="button"
              onClick={() => handleNavigateToAdmin('verifikasi')}
              className="w-full text-xs font-bold text-[#004A99] hover:underline flex items-center justify-center gap-1.5 py-1.5 cursor-pointer"
            >
              Lihat Panel Administrasi <ExternalLink className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
